// @flow
import { commitMutation, graphql } from 'react-relay';
import { ConnectionHandler } from 'relay-runtime';
import _get from 'lodash/get';
import CartPromoCodeAutoAddMutation from './CartPromoCodeAutoAddMutation';
import { trackEvent } from '../../utils/tracking';
import { purchaseWithPurchaseProductIds } from '../../components/_common/Cart/pwpConfig';

const mutation = graphql`
  mutation CartDeleteMutation($input: CartDeleteInput!) {
    cartDelete(input: $input) {
      deletedCartEntryId
      cart {
        id
        eventId
        event {
          id
          trinityPartyType
        }
        taxRate
        taxTotal
        autoShipEnabled
        autoShipIntervalUnit
        autoShipIntervalCount
        enrollingAsPc
        totalNumberOfItems
        viaQualifiedCartTotal
        subTotalPriceOfItems
        subTotalPriceOfItemsWithPromoCodes
        entries(first: 1000) {
          edges {
            node {
              id
              quantity
              product {
                id
                name
                price
                priceSale
                productId
                productPermissions {
                  oneCommerce {
                    preferredCustomer {
                      add
                    }
                  }
                }
                images {
                  squareFront
                  productCatalog
                  productFront
                  productBack
                }
                promoCodeDiscount {
                  case4Cause {
                    case6SKU
                    case12SKU
                    donationEligible
                    case12PriceUSD
                    case6PriceUSD
                  }
                }
              }
            }
          }
        }
        entriesV2(first: 1000) {
          edges {
            node {
              id
              quantity
              product {
                name
                id
                productId
                price
                finalPrice
                productPermissions {
                  oneCommerce {
                    preferredCustomer {
                      add
                    }
                  }
                }
                images {
                  squareFront
                  productCatalog
                  productFront
                  productBack
                }
                promoCodeDiscount {
                  case4Cause {
                    case6SKU
                    case12SKU
                    donationEligible
                    case12PriceUSD
                    case6PriceUSD
                  }
                }
              }
            }
          }
        }
        pcPotentialSavings
        promoCodeSavings
        promoCodeDiscountedItems {
          id
          nonDiscountedSkus {
            edges {
              node {
                id
                sku
                discountAmount
                productId
                quantity
                product {
                  id
                  productId
                  productURLslug
                  name
                  price
                  productPermissions {
                    oneCommerce {
                      preferredCustomer {
                        add
                      }
                    }
                  }
                  images {
                    squareFront
                    productCatalog
                    productFront
                    productBack
                  }
                }
              }
            }
          }
          discountedSkus {
            edges {
              node {
                id
                sku
                discountAmount
                productId
                quantity
                product {
                  id
                  productId
                  productURLslug
                  name
                  price
                  productPermissions {
                    oneCommerce {
                      preferredCustomer {
                        add
                      }
                    }
                  }
                  images {
                    squareFront
                    productCatalog
                    productFront
                    productBack
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

// update getViaQualifiedCartTotal to include wine packs
function getViaQualifiedCartTotal(itemBeingAdded, entries, qtyToDelete) {
  let qualifiedTotal = 0;
  entries.edges.forEach(entry => {
    const { product } = entry.node;
    const case4CauseDiscount = _get(
      product,
      'promoCodeDiscount.case4Cause.case6PriceUSD',
      null,
    );
    if (case4CauseDiscount) {
      qualifiedTotal += entry.node.quantity;
    }
  });
  const case4CauseDiscountOnAddedItem = _get(
    itemBeingAdded,
    'promoCodeDiscount.case4Cause.case6PriceUSD',
    null,
  );
  if (!case4CauseDiscountOnAddedItem) {
    return qualifiedTotal;
  }

  return qualifiedTotal - qtyToDelete;
}

function getOptimisticResponse(cartEntry, cart) {
  console.log('cart', cart);
  const { product } = cartEntry;
  const subTotal =
    cart.subTotalPriceOfItems -
    (product.priceSale ? product.priceSale : product.price);
  return {
    cartDelete: {
      deletedCartEntryId: cartEntry.id,
      cart: {
        id: cart.id,
        eventId: cart.eventId,
        event: cart.event,
        taxRate: cart.taxRate,
        taxTotal: product.categoryWine
          ? cart.taxTotal -
            (product.priceSale || product.price) *
              (cart.taxRates ? cart.taxRates.wine : cart.taxRate)
          : (product.priceSale || product.price) *
            (cart.taxRates ? cart.taxRates.merchandise : cart.taxRate),
        subTotalPriceOfItems: subTotal,
        totalNumberOfItems: cart.totalNumberOfItems - cartEntry.quantity,
        // update getViaQualifiedCartTotal to include wine packs
        viaQualifiedCartTotal: getViaQualifiedCartTotal(
          product,
          cart.entries,
          cartEntry.quantity,
        ),
        subTotalPriceOfItemsWithPromoCodes:
          cart.subTotalPriceOfItemsWithPromoCodes,
        entries: cart.entries,
      },
    },
  };
}

function sharedUpdater(store, cart, deletedId) {
  const cartProxy = store.get(cart.id);
  const conn = ConnectionHandler.getConnection(cartProxy, 'Cart_entries');
  ConnectionHandler.deleteNode(conn, deletedId);
}

// function onCompletedDefault({ environment, response }) {
//   console.log(response, 'in OnCompletedDefault CartDelteMutation');
//   return CartPromoCodeAutoAddMutation.commit(environment);
// }

const getCartItem = ({ cart, pwpProductId }) => {
  return cart.entries.edges.find(
    ({ node: cartEntry }) => cartEntry.product.productId === pwpProductId,
  );
};

function onCompletedDefault(defaultParams) {
  const { environment, response, user } = defaultParams;
  const { viaQualifiedCartTotal, entries } = response.cartDelete.cart;
  const purchaseWithPurchase = entries.edges.filter(updatedCartEntry => {
    const updatedCartEntryProductId =
      updatedCartEntry.node.product && updatedCartEntry.node.product.productId;
    return purchaseWithPurchaseProductIds.includes(updatedCartEntryProductId);
  });
  const purchaseWithPurchaseInvalid = viaQualifiedCartTotal < 6;
  if (purchaseWithPurchase.length && purchaseWithPurchaseInvalid) {
    const pwpProductId = purchaseWithPurchase[0].node.product.productId;
    const cartItem = getCartItem({
      cart: response.cartDelete.cart,
      pwpProductId,
    });
    // eslint-disable-next-line no-use-before-define
    commit(environment, response.cartDelete.cart, cartItem.node, user, false);
  }
}

function getOnCompleted(onCompleted, defaultParams) {
  //add tracking for remove from cart
  const { product, response, user } = defaultParams;
  trackEvent({
    user,
    event: 'Product Removed',
    properties: {
      product,
      cart: { ...response.cartDelete.cart },
      label: 'remove',
    },
  });
  if (onCompleted) {
    onCompleted();
  }
  onCompletedDefault(defaultParams);
  const { enrollingAsPc } = response.cartDelete.cart;
  // if (!enrollingAsPc) {
  //   return onCompletedDefault(defaultParams);
  // }
}

function commit(
  environment: Object,
  cart: Object,
  cartEntry: Object,
  user: Object,
  onCompleted: () => void,
) {
  const defaultParams = { cart, product: cartEntry.product, environment, user };
  return commitMutation(environment, {
    mutation,
    variables: {
      input: {
        id: cartEntry.id,
        productId: cartEntry.product.id,
      },
    },
    updater(store) {
      if (store && cart) {
        const payload = store.getRootField('cartDelete');
        if (payload) {
          const deletedId = payload.getValue('deletedCartEntryId');
          if (deletedId) {
            sharedUpdater(store, cart, deletedId);
          }
        }
      }
    },
    // optimisticUpdater(store) {
    //   // if (store && cart) {
    //   const payload = store.getRootField('cartDelete');
    //   if (payload) {
    //     const deletedId = payload.getValue('deletedCartEntryId');
    //     if (deletedId) {
    //       sharedUpdater(store, cart, deletedId);
    //     }
    //   }
    //   // }
    // },
    // optimisticResponse: getOptimisticResponse(cartEntry, cart),
    onCompleted: response =>
      getOnCompleted(onCompleted, { ...defaultParams, response }),
    onError: err => console.error(err),
  });
}

export default { commit };
